import { Product, dataKey } from "./product-types";
import { ATTR_IDS } from "./constants";
import { getColumnFilterKind, getColumnRegistryFilterValue, getColumnRegistrySortValue, getColumnSortKind } from "./column-registry";
import { tableFacetLabel, tableFacetValue, tableValuesMatchFilters, type TableFilterMode, type TableValue, type TableValueFilter } from "./table-engine/values";
import type { SortDeps } from "./sort-types";
import { matchesSearch } from "./search";

export type AttrFilters = Record<string, string[]>;

export type ColumnFilterMode = TableFilterMode;

export type ColumnFilter = TableValueFilter<string>;

export const COLUMN_FILTER_MODES: Array<{ value: ColumnFilterMode; label: string }> = [
  { value: "all", label: "Все значения" },
  { value: "filled", label: "Заполнено" },
  { value: "empty", label: "Пусто" },
  { value: "number_between", label: "От и до" },
  { value: "number_gt", label: "Больше" },
  { value: "number_lt", label: "Меньше" },
  { value: "number_eq", label: "Равно" },
  { value: "gt0", label: "Больше 0" },
  { value: "eq0", label: "Равно 0" },
  { value: "text_contains", label: "Текст содержит" },
  { value: "text_not_contains", label: "Текст не содержит" },
  { value: "text_starts_with", label: "Текст начинается" },
];

export const TEXT_COLUMN_FILTER_MODES = COLUMN_FILTER_MODES.filter(mode => (
  mode.value === "all" ||
  mode.value === "filled" ||
  mode.value === "empty" ||
  mode.value.startsWith("text_")
));

export const NUMBER_COLUMN_FILTER_MODES = COLUMN_FILTER_MODES.filter(mode => (
  mode.value === "all" ||
  mode.value === "filled" ||
  mode.value === "empty" ||
  mode.value.startsWith("number_") ||
  mode.value === "gt0" ||
  mode.value === "eq0"
));

export interface NumFilter {
  key: string;
  min: number | null;
  max: number | null;
}

export interface ViewAttrFilter {
  key: string;
  values: string[];
}

export interface ViewNumFilter {
  key: string;
  min: number | null;
  max: number | null;
}

export function applySearch(items: Product[], search: string): Product[] {
  if (!search) return items;
  return items.filter(p => matchesSearch(search, p.name, p.offer_id, String(p.sku ?? ""), String(p.id)));
}

export function applyAttrFilters(
  items: Product[],
  attrFilters: AttrFilters,
  attrData: Record<string, Record<string, string>>,
  skipKeys?: string[],
): Product[] {
  for (const [key, selected] of Object.entries(attrFilters)) {
    if (!selected?.length) continue;
    if (skipKeys?.includes(key)) continue;
    if (key === "type_name") {
      items = items.filter(p => p.type_name != null && selected.includes(p.type_name));
      continue;
    }
    const ids = ATTR_IDS[key] ?? [];
    if (!ids.length) continue;
    items = items.filter(p => {
      const attr = attrData[dataKey(p)] ?? {};
      return ids.some(id => attr[id] && selected.includes(attr[id]));
    });
  }
  return items;
}

export function applyNumFilters(
  items: Product[],
  numFilters: NumFilter[],
  deps: SortDeps,
): Product[] {
  for (const f of numFilters) {
    items = items.filter(p => {
      const v = getColumnRegistrySortValue(p, f.key, deps);
      if (f.min != null && v < f.min) return false;
      if (f.max != null && v > f.max) return false;
      return true;
    });
  }
  return items;
}

export function isTextColumnFilterKey(key: string): boolean {
  return getColumnFilterKind(key) === "text";
}

export function columnFilterModesForKey(key: string, kind?: "number" | "text") {
  return (kind ?? (isTextColumnFilterKey(key) ? "text" : "number")) === "text"
    ? TEXT_COLUMN_FILTER_MODES
    : NUMBER_COLUMN_FILTER_MODES;
}

export type ColumnFilterValue = TableValue;

export function getColumnFilterValue(product: Product, key: string, deps: SortDeps): ColumnFilterValue {
  return getColumnRegistryFilterValue(product, key, deps);
}

export function columnFilterFacetValue(value: ColumnFilterValue): string {
  return tableFacetValue(value);
}

export function columnFilterFacetLabel(value: string): string {
  return tableFacetLabel(value);
}

export function productMatchesColumnFilters(
  product: Product,
  filters: ColumnFilter[],
  deps: SortDeps,
  options?: { ignoreValueSelectionForKey?: string },
): boolean {
  return tableValuesMatchFilters(
    key => getColumnFilterValue(product, key, deps),
    filters,
    options,
  );
}

export function applyColumnFilters(items: Product[], filters: ColumnFilter[], deps: SortDeps): Product[] {
  if (filters.length === 0) return items;
  return items.filter(product => productMatchesColumnFilters(product, filters, deps));
}

/** Apply view config attr filters (e.g. from saved view), optionally skipping certain keys */
export function applyViewAttrFilters(
  items: Product[],
  viewAttrFilters: ViewAttrFilter[],
  attrData: Record<string, Record<string, string>>,
  skipKeys?: string[],
): Product[] {
  for (const f of viewAttrFilters) {
    if (!f.values.length) continue;
    if (skipKeys?.includes(f.key)) continue;
    if (f.key === "type_name") {
      items = items.filter(p => p.type_name != null && f.values.includes(p.type_name));
      continue;
    }
    const ids = ATTR_IDS[f.key] ?? [];
    if (!ids.length) continue;
    items = items.filter(p => {
      const attr = attrData[dataKey(p)] ?? {};
      return ids.some(id => attr[id] && f.values.includes(attr[id]));
    });
  }
  return items;
}

/** Apply view config numeric filters */
export function applyViewNumFilters(
  items: Product[],
  viewNumFilters: ViewNumFilter[],
  deps: SortDeps,
): Product[] {
  for (const f of viewNumFilters) {
    items = items.filter(p => {
      const v = getColumnRegistrySortValue(p, f.key, deps);
      if (f.min != null && v < f.min) return false;
      if (f.max != null && v > f.max) return false;
      return true;
    });
  }
  return items;
}

export function sortItems(
  items: Product[],
  sortKey: string,
  sortDir: "asc" | "desc",
  deps: SortDeps,
): Product[] {
  if (getColumnSortKind(sortKey) === "text") {
    return items
      .map((product, index) => ({
        product,
        index,
        value: String(getColumnRegistryFilterValue(product, sortKey, deps) ?? ""),
      }))
      .sort((a, b) => {
        const cmp = a.value.localeCompare(b.value, "ru", { sensitivity: "base", numeric: true });
        if (cmp !== 0) return sortDir === "asc" ? cmp : -cmp;
        return a.index - b.index;
      })
      .map(item => item.product);
  }
  return items
    .map((product, index) => ({
      product,
      index,
      value: getColumnRegistrySortValue(product, sortKey, deps),
    }))
    .sort((a, b) => {
      const cmp = a.value - b.value;
      if (cmp !== 0) {
        return sortDir === "desc" ? -cmp : cmp;
      }
      return a.index - b.index;
    })
    .map(item => item.product);
}
